import { useEffect, useState } from "react";
import { ClipboardList, HeartHandshake, Home, Send } from "lucide-react";
import { api, apiError, scrollToId } from "@/lib/helpers";
import { ADOPTION_STEPS } from "@/lib/data";
import { Reveal } from "@/components/Reveal";
import { toast } from "sonner";

const STEP_ICONS = [ClipboardList, HeartHandshake, Home];

export default function Adopt({ adoptTarget, setAdoptTarget }) {
  const [animals, setAnimals] = useState([]);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", city: "", animal_name: "", housing: "House with a yard", experience: "Experienced", household: "", message: "" });

  useEffect(() => {
    api.get("/animals").then(({ data }) => setAnimals(data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (adoptTarget) {
      setForm((f) => ({ ...f, animal_name: adoptTarget.name }));
      setTimeout(() => scrollToId("adopt"), 120);
    }
  }, [adoptTarget]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !/^\S+@\S+\.\S+$/.test(form.email)) {
      toast.error("We need your name and a valid email to start your application.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/adoptions", form);
      toast.success("Application received!", { description: form.animal_name ? `Our adoption team will reach out about ${form.animal_name} within 2 days.` : "Our adoption team will reach out within 2 days." });
      setForm({ name: "", email: "", phone: "", city: "", animal_name: "", housing: "House with a yard", experience: "Experienced", household: "", message: "" });
      setAdoptTarget(null);
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  const inputCls = "w-full rounded-xl border border-stone-300 bg-white px-4 py-3 text-sm outline-none transition-all focus:ring-2 focus:ring-gold focus:border-gold";

  return (
    <section id="adopt" className="relative bg-tinted py-20 sm:py-24 lg:py-28">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal className="mb-12 max-w-2xl">
          <p className="eyebrow">Adoption</p>
          <h2 className="mt-3 font-serif text-2xl sm:text-3xl lg:text-4xl text-charcoal tracking-tight">
            Three steps between our door and theirs.
          </h2>
        </Reveal>

        <div className="grid gap-10 lg:grid-cols-12">
          <div className="lg:col-span-5 space-y-4">
            {ADOPTION_STEPS.map((s, i) => {
              const Icon = STEP_ICONS[i];
              return (
                <Reveal key={s.n} delay={i * 0.1}>
                  <div className="flex gap-5 rounded-3xl bg-white border border-stone-200 p-6 shadow-sm">
                    <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-pine text-gold-soft">
                      <Icon className="h-5 w-5" />
                    </span>
                    <div>
                      <p className="font-mono text-xs text-gold-deep">STEP {s.n}</p>
                      <h3 className="mt-1 font-serif text-xl text-charcoal">{s.title}</h3>
                      <p className="mt-1.5 text-sm text-bark leading-relaxed">{s.desc}</p>
                    </div>
                  </div>
                </Reveal>
              );
            })}
            <Reveal delay={0.3}>
              <div className="rounded-3xl bg-pine p-6 text-sand">
                <p className="font-serif text-lg">Adoption fees</p>
                <p className="mt-2 font-mono text-sm text-sand/80" data-testid="adoption-fees">
                  Dogs $150 · Cats $75 — includes spay/neuter, vaccines & microchip.
                </p>
                <p className="mt-3 text-xs text-sand/60">Fees go straight back into the next rescue's care.</p>
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.15} className="lg:col-span-7">
            <form onSubmit={submit} data-testid="adoption-form" className="rounded-3xl bg-white border border-stone-200 p-6 sm:p-10 shadow-sm">
              <h3 className="font-serif text-2xl text-charcoal">Adoption application</h3>
              <p className="mt-1 text-sm text-bark">Take your time — honest beats perfect.</p>
              <div className="mt-6 grid gap-4 sm:grid-cols-2">
                <input data-testid="adopt-name-input" required placeholder="Full name *" value={form.name} onChange={set("name")} className={inputCls} />
                <input data-testid="adopt-email-input" required type="email" placeholder="Email *" value={form.email} onChange={set("email")} className={inputCls} />
                <input data-testid="adopt-phone-input" placeholder="Phone" value={form.phone} onChange={set("phone")} className={inputCls} />
                <input data-testid="adopt-city-input" placeholder="City / town" value={form.city} onChange={set("city")} className={inputCls} />
                <select data-testid="adopt-animal-select" value={form.animal_name} onChange={set("animal_name")} className={inputCls}>
                  <option value="">Who caught your eye?</option>
                  {animals.map((a) => <option key={a.id} value={a.name}>{a.name} — {a.breed}</option>)}
                  <option value="Not sure yet">Not sure yet — help me find a match</option>
                </select>
                <select data-testid="adopt-housing-select" value={form.housing} onChange={set("housing")} className={inputCls}>
                  {["House with a yard", "Apartment", "Farm / ranch", "Living situation varies"].map((h) => <option key={h}>{h}</option>)}
                </select>
                <select data-testid="adopt-experience-select" value={form.experience} onChange={set("experience")} className={`${inputCls} sm:col-span-2`}>
                  {["Experienced", "First-time pet parent", "Rescue veteran", "Grew up with animals"].map((x) => <option key={x}>{x}</option>)}
                </select>
                <textarea data-testid="adopt-household-input" rows={3} placeholder="Tell us about your household — people, pets, hours away from home…" value={form.household} onChange={set("household")} className={`${inputCls} sm:col-span-2`} />
              </div>
              <button data-testid="adopt-submit-btn" type="submit" disabled={busy}
                className="mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-pine py-4 font-bold text-sand transition-all hover:bg-pine-mid hover:scale-[1.01] active:scale-95 disabled:opacity-60">
                <Send className="h-4 w-4" /> {busy ? "Sending…" : "Submit application"}
              </button>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
